/**
 * 섬머노트에 붙여넣은 «블록을 감싼 링크» 가 빈 링크로 쪼개지는 것 막기 (2026-10-20)
 *
 *  우리 사이트 화면(투어 카드 등)을 복사하면 클립보드에 <a href><div>…</div></a> 가 담긴다(HTML5 에서 정상 구조).
 *  그런데 섬머노트의 줄은 <p> 이고, 크롬은 <p> 안에 블록을 둘 수 없어 붙여넣을 때 링크를 쪼갠다
 *  → <p><a href></a></p>(빈 링크) + 링크 밖으로 밀려난 내용. 저장하면 그대로 남는다(마퍼가 contents #1384).
 *  같은 내용을 <div> 줄에 붙여넣으면 크롬이 쪼개지 않는다(실측) → 붙여넣기 직전에 커서가 있는 <p> 만 <div> 로 바꾸고,
 *  붙여넣기 자체는 브라우저 기본 동작에 맡긴다(클립보드 정리·보안 처리를 우리가 다시 만들지 않는다).
 *  <p> 와 <div> 는 본문 CSS 에서 생김새가 같다(전역 p 여백 규칙 없음).
 *
 *  ⚠️ CK에디터는 해당 없음 — 링크를 글자 속성으로 옮겨 담아 쪼개지지 않는다(실측). 섬머노트 전용
 */

const BLOCK_TAGS = [
  'address', 'article', 'aside', 'blockquote', 'dd', 'details', 'div', 'dl', 'dt', 'fieldset', 'figcaption', 'figure',
  'footer', 'form', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'header', 'hr', 'li', 'main', 'nav', 'ol', 'p', 'pre', 'section',
  'table', 'ul'
]
const BLOCK_IN_LINK = BLOCK_TAGS.map((tag) => `a ${tag}`).join(',')

/** 클립보드 HTML 에 «블록을 감싼 링크» 가 있는가 — DOMParser 로 만든 문서는 스크립트·이미지를 불러오지 않는다 */
export function hasBlockInsideLink(html: string | null | undefined): boolean {
  if (!html || !/<a[\s>]/i.test(html)) return false
  return !!new DOMParser().parseFromString(html, 'text/html').querySelector(BLOCK_IN_LINK)
}

/**
 * paste 이벤트 안(기본 동작 전)에서 부른다. 기본 동작은 막지 않는다.
 *  해당 없는 붙여넣기(링크 없음·인라인 링크·커서가 <p> 밖)는 아무것도 바꾸지 않는다
 */
export function keepBlockLinksOnPaste(event: ClipboardEvent | null | undefined, editable: Element | null | undefined): void {
  if (!event || !editable) return
  if (!hasBlockInsideLink(event.clipboardData?.getData('text/html'))) return
  const selection = window.getSelection()
  if (!selection || !selection.rangeCount) return
  const range = selection.getRangeAt(0)
  if (!editable.contains(range.startContainer) || !editable.contains(range.endContainer)) return

  const paragraphOf = (node: Node) => {
    const p = (node.nodeType === Node.ELEMENT_NODE ? node as Element : node.parentElement)?.closest('p')
    return p && p !== editable && editable.contains(p) ? p : null
  }
  const targets = [...new Set([paragraphOf(range.startContainer), paragraphOf(range.endContainer)])]
    .filter((p): p is HTMLParagraphElement => !!p)
  if (!targets.length) return

  // 노드를 옮기면 살아 있는 범위가 흐트러지므로 경계를 먼저 받아 두고, 바꾼 뒤 다시 건다(글자 노드는 같은 것을 옮기므로 그대로 쓴다)
  let { startContainer, startOffset, endContainer, endOffset } = range
  for (const p of targets) {
    const div = document.createElement('div')
    for (const attr of Array.from(p.attributes)) div.setAttribute(attr.name, attr.value)
    while (p.firstChild) div.appendChild(p.firstChild)
    p.replaceWith(div)
    if (startContainer === p) startContainer = div
    if (endContainer === p) endContainer = div
  }
  const restored = document.createRange()
  restored.setStart(startContainer, startOffset)
  restored.setEnd(endContainer, endOffset)
  selection.removeAllRanges()
  selection.addRange(restored)
}
